import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Download } from 'lucide-react';
import { useAlertsInbox, useDashboardAcademic, useStudentsList } from '../../api/hooks';
import { useUserInfo } from '../../../react-query/queries';
import { PageHeader, PanelCard, Spinner, ErrorPanel, EmptyState, Segmented, Button } from '../../components/ui';
import TopBarActions from '../../../components/Header/TopBarActions';
import { ChartLine } from 'lucide-react';
import KpiGrid from './sections/KpiGrid';
import QuickActions from './sections/QuickActions';
import TrendCard from './sections/TrendCard';
import DistributionCard from './sections/DistributionCard';
import GradesCard from './sections/GradesCard';
import SubjectsCard from './sections/SubjectsCard';
import SupportCard from './sections/SupportCard';
import WeakChaptersCard from './sections/WeakChaptersCard';
import RecentExamsCard from './sections/RecentExamsCard';
import RemediationCard from './sections/RemediationCard';
import { countWord, greeting, todayEyebrow } from './sections/format';
import CoachLaunchButton from '../student_360/coach/CoachLaunchButton';
import { useAuth } from '../../../utils/Auth/AuthProvider';

const WEEKS = ['4', '8', '12', '26'] as const;
// Design grid: twelve columns above 1180px, two columns (span 6) from 820px, one column below.
const span = (n: 3 | 4 | 5 | 7 | 8 | 12) => (n === 12
  ? 'col-span-12'
  : `col-span-12 min-[820px]:col-span-6 ${{ 3: 'min-[1180px]:col-span-3', 4: 'min-[1180px]:col-span-4', 5: 'min-[1180px]:col-span-5', 7: 'min-[1180px]:col-span-7', 8: 'min-[1180px]:col-span-8' }[n]}`);

interface Props { title: string; scopeNote?: string; focus?: ReactNode; /** Shown between the header and the grid (e.g. the written-answers link). */ aside?: ReactNode; showQuickActions?: boolean }

export default function AcademicDashboard({ title, scopeNote, focus, aside, showQuickActions = true }: Props) {
  const { role } = useAuth();
  const [weeks, setWeeks] = useState<(typeof WEEKS)[number]>('12');
  const q = useDashboardAcademic(Number(weeks));
  const user = useUserInfo();
  const alerts = useAlertsInbox({}).data?.results;
  const students = useStudentsList({}).data?.results ?? [];
  const first = (user.data?.first_name || user.data?.name?.split(' ')[0] || '').trim();
  const hello = first ? `${greeting()}, ${first}` : greeting();

  const header = (sub: ReactNode) => (
    <PageHeader eyebrow={todayEyebrow()} title={hello} trail={[]} subtitle={sub}
      actions={<>
        <CoachLaunchButton inline label="Ask the coach"
          scope={role === 'teacher' ? { type: 'mine', label: 'your classes' } : { type: 'school' }} />
        <Segmented label="Time window" value={weeks} onChange={setWeeks} options={WEEKS.map((w) => ({ key: w, label: `${w}w` }))} />
        <Button variant="ghost" icon={<Download size={18} strokeWidth={1.8} />} onClick={() => window.print()}>Export</Button>
      </>} />
  );

  const d = q.data;
  const delta = d?.headline.delta_4w;
  // "Three students need you today": the students with a critical alert open, the same figure as the focus card.
  const needs = new Set((alerts ?? []).filter((a) => a.severity === 'critical').map((a) => a.student_id)).size;
  const sub = !d ? title : [
    delta == null ? `Last ${d.weeks} weeks.` : delta >= 0 ? 'Scores are up this month.' : 'Scores dipped this month.',
    needs > 0 ? `${countWord(needs)} ${needs === 1 ? 'student needs' : 'students need'} you today.` : null,
    scopeNote ? `Showing ${scopeNote}.` : null,
  ].filter(Boolean).join(' ');

  const noData = !!d && d.headline.students_with_data === 0;
  const status = q.isLoading ? (
    <PanelCard><div className="flex justify-center py-8"><Spinner size="md" /></div></PanelCard>
  ) : q.isError || !d ? (
    <PanelCard><ErrorPanel body="Could not load the dashboard. Try again." /></PanelCard>
  ) : noData ? (
    <PanelCard><EmptyState icon={<ChartLine size={24} />} title="No exam data in this window" body="Numbers appear once students take exams. Try a longer window." /></PanelCard>
  ) : null;

  // One stable tree so the page header (and its h1) is not remounted when the data arrives.
  return (
    <>
      <TopBarActions>
        <Link to="/exams/add-exam" className="order-last inline-flex min-h-[44px] items-center gap-2 rounded-[12px] bg-accent px-4 text-sm font-semibold text-navy no-underline hover:text-navy hover:brightness-105">
          <Plus size={18} strokeWidth={1.8} aria-hidden />New exam
        </Link>
      </TopBarActions>
      {header(sub)}
      {aside && <div className="mb-[18px]">{aside}</div>}
      <div className="grid grid-cols-12 gap-[18px]">
        {focus && <div className={`${span(5)} flex flex-col [&>section]:flex-1`}>{focus}</div>}
        {status ? <div className={focus ? span(7) : span(12)}>{status}</div> : (
          <div className={focus ? span(7) : span(12)}><KpiGrid d={d!} /></div>
        )}
        {d && !status && (
          <>
            {showQuickActions && <QuickActions />}
            <TrendCard d={d} className={span(8)} />
            <DistributionCard d={d} className={span(4)} />
            <GradesCard d={d} className={span(7)} />
            <SubjectsCard d={d} className={span(5)} />
            <SupportCard students={students} className={span(4)} />
            <WeakChaptersCard d={d} className={span(4)} />
            <RecentExamsCard d={d} className={span(4)} />
            <RemediationCard d={d} className={span(12)} />
          </>
        )}
      </div>
    </>
  );
}
